import dayjs, { type Dayjs } from 'dayjs';

// The one display format every date field in this app uses (Spain: day first).
export const DATE_FORMAT = 'DD/MM/YYYY';

const SEPARATED_DATE = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/;
const DIGITS_ONLY_DATE = /^(\d{2})(\d{2})(\d{2}|\d{4})$/;

/**
 * Reads a date staff typed into a date field, day first: "15/11/2026", "5/1/2026", "15-11-2026",
 * "15.11.26", "15112026" or "151126". A two-digit year is in this century. Returns the start of
 * that day in the browser's zone, or null for anything that is not a real calendar day (31/02,
 * 00/11, a half-typed date), so the field never commits a date the person did not mean.
 */
export const parseTypedDate = (text: string): Dayjs | null => {
  const trimmed = text.trim();
  const match = SEPARATED_DATE.exec(trimmed) ?? DIGITS_ONLY_DATE.exec(trimmed);
  if (!match?.[1] || !match[2] || !match[3]) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);

  // Date rolls an out-of-range day or month over (31/02 becomes 03/03), so a date is real only
  // when every part survives the round trip unchanged.
  const candidate = dayjs(new Date(year, month - 1, day));
  const isRealDay =
    candidate.isValid() &&
    candidate.date() === day &&
    candidate.month() === month - 1 &&
    candidate.year() === year;
  return isRealDay ? candidate : null;
};

/**
 * ArrowUp/ArrowDown in a date field: `days` from the date being typed when it is a real day, else
 * from the field's committed date, else from today.
 */
export const stepTypedDate = (
  draft: string | null,
  committed: Dayjs | null | undefined,
  days: number,
  today: Dayjs
): Dayjs => {
  const typed = draft === null ? null : parseTypedDate(draft);
  const base = typed ?? (committed?.isValid() ? committed : null) ?? today;
  return base.add(days, 'day');
};
